// White-label prequalification — runtime theme provider.
//
// Given a resolved partner skin, sets the `--pq-*` CSS custom properties
// (which the funnel's re-pointed Tailwind tokens read) on a wrapper element
// and loads the partner's display + body web fonts. No brand → renders its
// children with NO wrapper and NO variables, so the NATIVE funnel resolves
// every token to its baked-in hex — byte-identical to before the re-point.

import { useEffect, type CSSProperties, type ReactNode } from "react";
import type { PartnerBrandSkin } from "../../api/partnerBrand";

// ── hex helpers ─────────────────────────────────────────────────────────

function clampByte(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function parseHex(hex: string): [number, number, number] {
  let h = hex.replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const num = parseInt(h, 16);
  if (Number.isNaN(num)) return [0, 0, 0];
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

function toHex([r, g, b]: [number, number, number]): string {
  return "#" + [r, g, b].map((v) => clampByte(v).toString(16).padStart(2, "0")).join("");
}

/** Mix `hex` toward `target` by `amount` (0–1). */
function mix(hex: string, target: [number, number, number], amount: number): string {
  const [r, g, b] = parseHex(hex);
  const [tr, tg, tb] = target;
  return toHex([r + (tr - r) * amount, g + (tg - g) * amount, b + (tb - b) * amount]);
}

/** Lighten a hex color toward white by `amount` (0–1). */
function lighten(hex: string, amount: number): string {
  return mix(hex, [255, 255, 255], amount);
}

/** Darken a hex color toward black by `amount` (0–1). */
function darken(hex: string, amount: number): string {
  return mix(hex, [0, 0, 0], amount);
}

// ── font + variable derivation ──────────────────────────────────────────

function quoted(name: string): string {
  return /\s/.test(name) ? `'${name}'` : name;
}

function displayStack(font: string): string {
  return `${quoted(font)}, Georgia, 'Times New Roman', serif`;
}

function bodyStack(font: string): string {
  return `${quoted(font)}, system-ui, -apple-system, 'Segoe UI', sans-serif`;
}

/** The `--pq-*` variables derived from the 8 skin color tokens + 2 fonts. */
function buildThemeVars(skin: PartnerBrandSkin): CSSProperties {
  const c = skin.colors;
  const display = displayStack(skin.fontDisplay);
  const vars: Record<string, string> = {
    "--pq-paper": c.background,
    "--pq-cream": c.surface,
    "--pq-ink-black": c.foreground,
    "--pq-ink": c.foreground,
    "--pq-ink-mid": c.bodyText,
    "--pq-ink-faint": lighten(c.bodyText, 0.28),
    "--pq-line": c.hairline,
    "--pq-gold": c.accent,
    "--pq-gold-highlight": lighten(c.accent, 0.22),
    "--pq-gold-shadow": darken(c.accent, 0.24),
    "--pq-gold-deep": darken(c.accent, 0.14),
    "--pq-gold-soft": lighten(c.accent, 0.55),
    "--pq-gold-tint": lighten(c.accent, 0.82),
    // Chrome (header/footer) tones — the shell reads these directly.
    "--pq-primary": c.primary,
    "--pq-pressed": c.pressed,
    // Fonts: money figures ride the partner display serif; body prose the
    // partner body face; the wordmark the display face.
    "--pq-font-display": display,
    "--pq-font-body": bodyStack(skin.fontBody),
    "--pq-font-serif": display,
  };
  return vars as CSSProperties;
}

// ── dynamic font loading ────────────────────────────────────────────────

const PARTNER_FONT_LINK_ID = "pq-partner-fonts";

/** Inject a Google Fonts stylesheet for the partner's display + body faces.
 *  Deduped by a stable id; the href is updated in place when a different
 *  partner resolves. NEVER added to index.html / the global @import, so the
 *  native funnel keeps its own font set. */
function loadPartnerFonts(display: string, body: string): void {
  if (typeof document === "undefined") return;
  const families = [display, body]
    .map((f) => f.trim())
    .filter(Boolean)
    .map((f) => `family=${f.replace(/\s+/g, "+")}:wght@400;500;600;700`)
    .join("&");
  if (!families) return;
  const href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
  const existing = document.getElementById(PARTNER_FONT_LINK_ID) as HTMLLinkElement | null;
  if (existing) {
    if (existing.href !== href) existing.href = href;
    return;
  }
  const link = document.createElement("link");
  link.id = PARTNER_FONT_LINK_ID;
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
}

// ── provider ────────────────────────────────────────────────────────────

export function PartnerThemeProvider({
  brand,
  children,
}: {
  brand: PartnerBrandSkin | null;
  children: ReactNode;
}) {
  // A buyer team's Phoenix-skin site is a NATIVE render whatever the DTO
  // carries. PrequalBrandContext already normalises it to `null`; this is the
  // belt for a consumer that hands the raw skin straight in.
  const skin = brand && brand.usesPhoenixSkin !== true ? brand : null;

  useEffect(() => {
    if (!skin) return;
    loadPartnerFonts(skin.fontDisplay, skin.fontBody);
  }, [skin]);

  // Native (no partner) — no wrapper, no variables. Every re-pointed token
  // resolves to its baked-in fallback: identical to the pre-re-point funnel.
  if (!skin) return <>{children}</>;

  return (
    <div
      data-partner-theme={skin.slug}
      style={{ ...buildThemeVars(skin), fontFamily: "var(--pq-font-body)" }}
    >
      {children}
    </div>
  );
}

/** Subtle muted notice rendered above the native funnel when a `:slug` route
 *  named an unknown / inactive partner. */
export function PartnerFallbackNotice() {
  return (
    <p
      data-testid="prequal-partner-fallback"
      className="text-caption text-ink-faint bg-cream border border-line rounded-md px-space-3 py-space-2 mt-0 mb-space-4"
    >
      We couldn’t find that partner — showing the Phoenix Capital experience.
    </p>
  );
}
